import { useAuth } from '@youversion/auth';
import { Box, Button } from '@youversion/design-system-components';
import Spinner from '@youversion/design-system-icons/lib/Spinner';
import { AlertBar } from 'components/layout/alert-bar';
import Footer from 'components/layout/footer';
import { BackButton } from 'components/ui/buttons';
import ConfirmationDialog from 'components/ui/dialogs/ConfirmationDialog';
import Header from 'components/ui/modal/Header';
import GiveForm from 'components/views/giving/give-form';
import { format } from 'date-fns';
import { useError } from 'hooks/useError';
import { useQueryParams } from 'hooks/useQueryParams';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { donationValidation } from 'utils/constants';
import { firebaseLogEvent } from 'utils/firebase';
import { setFlutterNav } from 'utils/flutter/setFlutterNav';
import { useAmount } from 'utils/store/amount';
import { useCurrency } from 'utils/store/currency';
import { useFrequency } from 'utils/store/frequency';
import { useFund } from 'utils/store/fund';
import { usePaymentDate } from 'utils/store/paymentDate';
import { usePaymentMethod } from 'utils/store/paymentMethod';

import {
  useDeleteSubscriptionMutation,
  useGetSubscriptionQuery,
  useUpdateSubscriptionMutation,
} from '@/graphql/subscriptions';

interface Props {
  subscriptionId: string;
}

export default function GiveScheduled({ subscriptionId }: Props) {
  const { t } = useTranslation('common');
  const router = useRouter();
  const { selectedFrequency } = useFrequency();
  const { getFundName, selectedFund } = useFund();
  const { selectedCurrency } = useCurrency();
  const { selectedAmount } = useAmount();
  const { getPaymentMethod, paymentMethodType } = usePaymentMethod();
  const { setPaymentDate, selectedPaymentDate } = usePaymentDate();
  const { mutate: deleteSubscription, isLoading: isDeleteSubscriptionLoading } = useDeleteSubscriptionMutation();
  const { user } = useAuth();
  const { hide_nav: hideNav, utm_campaign, utm_content, utm_medium, utm_source } = useQueryParams();
  const { reportError } = useError();
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const errorTag = 'Edit Manage Giving';
  const { data, isLoading } = useGetSubscriptionQuery({
    subscriptionId: parseInt(subscriptionId, 10),
  });
  const { mutate: updateSubscription, isLoading: isUpdateSubscriptionLoading } = useUpdateSubscriptionMutation({
    onError: err => {
      reportError(`${errorTag} [subscriptionsResourceResponse] Error:`, err);
    },
    onSuccess: () => {
      void router.replace(`/schedule${hideNav ? '?hide_nav=true' : ''}`);
    },
  });
  const amountInputRef = useRef<HTMLInputElement>(null);
  const [isAmountInputFocused, setIsAmountInputFocused] = useState(false);
  const [paymentMethodDialogOpen, setPaymentMethodDialogOpen] = useState(false);
  const [processDateDialogOpen, setProcessDateDialogOpen] = useState(false);
  const [frequencyDialogOpen, setFrequencyDialogOpen] = useState(false);
  const [fundDialogOpen, setFundDialogOpen] = useState(false);

  const amountInvalid = useMemo(
    () =>
      selectedAmount === '' || selectedAmount === '.' || parseFloat(selectedAmount) < donationValidation.minimumAmount,
    [selectedAmount]
  );

  function blurInput() {
    amountInputRef.current?.blur();
    setIsAmountInputFocused(false);
  }

  function focusInput() {
    amountInputRef.current?.focus();
    setIsAmountInputFocused(true);
  }

  useEffect(() => {
    if (data && !isLoading) {
      const subscriptionAttributes = data.getSubscription;
      if (subscriptionAttributes?.nextPaymentDate) {
        void setPaymentDate(subscriptionAttributes.nextPaymentDate);
      }
    }
  }, [data, isLoading, setPaymentDate, subscriptionId]);

  useEffect(() => {
    setFlutterNav(false);
  }, []);

  async function patchDonation() {
    const paymentMethodId = getPaymentMethod();
    if (!paymentMethodId) {
      return;
    }
    updateSubscription({
      subscriptionId: parseInt(subscriptionId, 10),
      subscriptionPatchInput: {
        amount: selectedAmount,
        frequencyId: selectedFrequency,
        fundId: selectedFund,
        nextPaymentDate: selectedPaymentDate.toISOString(),
        paymentMethodId: parseInt(paymentMethodId, 10),
        utmCampaign: utm_campaign,
        utmContent: utm_content,
        utmMedium: utm_medium,
        utmSource: utm_source,
      },
    });
  }

  async function handleSaveClick() {
    void patchDonation();
  }

  async function handleConfirmClick() {
    await firebaseLogEvent('GivingButtonTap', { label: 'Delete', screen: 'giving_schedule_detail' });
    setConfirmationDialogOpen(false);
    deleteSubscription(
      { subscriptionId: parseInt(subscriptionId, 10) },
      {
        onError: error => {
          reportError(`${errorTag} [subscriptionsResource] Error:`, error);
        },
        onSuccess: () => {
          void router.replace(`/schedule${hideNav ? '?hide_nav=true' : ''}`);
        },
      }
    );
  }

  useEffect(() => {
    firebaseLogEvent('GivingScreenView', { screen: 'giving_schedule_detail' });
    firebaseLogEvent('EditScheduleStarted', {
      amount: selectedAmount,
      currency: selectedCurrency,
      frequency: selectedFrequency,
      fund: getFundName(selectedFund),
      logged_in: user ? 'true' : 'false',
      payment_method: paymentMethodType?.toLowerCase() ?? '',
      start_date: format(selectedPaymentDate, 'yyyy-MM-dd'),
      yv_id: user?.id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Header
        left={
          <BackButton
            onClick={async () => {
              await firebaseLogEvent('GivingButtonTap', { label: 'Back', screen: 'giving_schedule_detail' });
              router.back();
            }}
          />
        }
        right={<></>}
        title={t('giving.titles.manage_gift')}
      />
      <Box className='m-2'>
        <AlertBar />
        <GiveForm
          amountInputRef={amountInputRef}
          blurInput={blurInput}
          focusInput={focusInput}
          frequencyDialogOpen={frequencyDialogOpen}
          fundDialogOpen={fundDialogOpen}
          isAmountInputFocused={isAmountInputFocused}
          isScheduledGift={true}
          paymentMethodDialogOpen={paymentMethodDialogOpen}
          processDateDialogOpen={processDateDialogOpen}
          screen='giving_schedule_detail'
          setFrequencyDialogOpen={setFrequencyDialogOpen}
          setFundDialogOpen={setFundDialogOpen}
          setPaymentMethodDialogOpen={setPaymentMethodDialogOpen}
          setProcessDateDialogOpen={setProcessDateDialogOpen}
        />
        <Box className='flex mt-3 gap-1.5'>
          <Button
            className='max-w-full flex justify-center'
            disabled={amountInvalid || isUpdateSubscriptionLoading}
            onClick={() => {
              firebaseLogEvent('GivingButtonTap', { label: 'Save', screen: 'giving_schedule_detail' });
              handleSaveClick();
            }}
            variant='primary'
          >
            {isUpdateSubscriptionLoading ? <Spinner dur={0.9} /> : t('giving.cta.save')}
          </Button>
          <Button
            className='max-w-full flex justify-center focus:ring-alert dark:focus:ring-alert-dark'
            disabled={isDeleteSubscriptionLoading}
            onClick={() => {
              firebaseLogEvent('GivingButtonTap', { label: 'Confirm Cancel', screen: 'giving_schedule_detail' });
              setConfirmationDialogOpen(true);
            }}
            variant='risk:stroked'
          >
            {isDeleteSubscriptionLoading ? (
              <Spinner dur={0.9} />
            ) : (
              t('giving.cta.cancel_scheduled_gift', 'Cancel Scheduled Gift')
            )}
          </Button>
        </Box>
      </Box>
      <Footer screen='giving_schedule_detail' />
      <ConfirmationDialog
        cancelLabel={t('giving.cta.cancel', 'Cancel')}
        confirmLabel={t('giving.cta.delete', 'Delete')}
        message={t(
          'giving.messages.confirm_delete_scheduled_gift',
          'Are you sure you want to delete this scheduled gift?'
        )}
        onCancelClick={() => {
          firebaseLogEvent('GivingButtonTap', { label: 'Cancel', screen: 'giving_schedule_detail' });
          setConfirmationDialogOpen(false);
        }}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirmClick={handleConfirmClick}
        open={confirmationDialogOpen}
        testId='cancel-dialog-overlay'
      />
    </>
  );
}
